import { KrRelationshipList } from '@workboard/ui-relationships';
import { FederatedAdoModal } from './krDetails/FederatedAdoModal';
import { ThemeProvider, ScopedCssBaseline, styled, CircularProgress, Box } from '@mui/material';
import React, { lazy, Suspense, useCallback, useState } from 'react';
import { GlobalStyle, theme } from './theme/theme';
import { okrDetailConstants } from '../constants/okr/okrDetails.constants';
import { isReadOnlyUserLicenseType } from '../utils/common';

const ActionPlanDialog = lazy(() => import('./krDetails/header/ActionPlanDialog'));

const StyledCssBaseline = styled(ScopedCssBaseline)({
  'body #new-related-work-area &, body #edit-related-work-area &': {
    // workaround for conflicts
    '& label': {
      top: '-10px',
      left: '-13px',
    },
  },
});

const AdoModalLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
      width: '100%',
    }}
  >
    <CircularProgress />
  </Box>
);

export type KrRelationshipsProps = {
  metric: any;
  eventProperties: {
    location: 'KR_PAGE' | 'KR_EDIT_SIDEPANEL';
    subLocation: 'KRR_COMPONENT' | 'COAUTHOR_BUTTON' | 'KEBAB_MENU';
  };
};

function KrRelationships({ metric, eventProperties }: KrRelationshipsProps) {
  const getCurrentUser = (userData: any, isReadOnly: boolean) => ({
    id: userData?.user_id,
    fullName: `${userData?.first_name} ${userData?.last_name}`,
    picture: userData.picture,
    isReadOnly,
  });

  const [actionPlanDialogOpen, setActionPlanDialogOpen] = useState(false);

  const workboardUrl = `${window.location.origin}/wb/vnext/okr`;
  const isSuggestionEnabled = (window?.wbConfig.isKrRelationshipAISuggestionsEnabled || false) as boolean;
  const isReadonlyUser = isReadOnlyUserLicenseType();
  const isSemEnabled = (window?.wbConfig?.isSemEnabled || false) as boolean;
  const isWsActionPlanEnabled = !!window?.wbConfig?.isWsActionPlanEnabled;
  const isCoauthorEnabled = !!window?.wbConfig?.coauthorEnabled;
  let canEdit = metric?.canEdit || false;
  if (isReadonlyUser) {
    canEdit = false;
  }
  const isIndividualGoal = Number(metric?.goalType) === okrDetailConstants.GOAL_TYPE_INDIVIDUAL;

  const handleActionPlanClick = useCallback(() => {
    setActionPlanDialogOpen(true);
  }, []);

  const handleCloseActionPlan = useCallback(
    ({ status, workstreamId, hasRelationship }) => {
      setActionPlanDialogOpen(false);

      if (status !== 'published') {
        return;
      }

      if (hasRelationship) {
        const channel = new BroadcastChannel('ai:actionItemUpdated');

        channel.postMessage(metric.id);
      }

      const message = 'The action plan was successfully published';
      const url = `${window.wbConfig.baseUrl}/activity/workstreams?id=${workstreamId}`;
      const linkStyles = 'color: #0072ab; font-weight: bold; white-space: nowrap;';
      const link = workstreamId
        ? `<br /><a href="${url}" target="_blank" rel="noopener noreferrer" style="${linkStyles}">View items</a>`
        : '';

      toastr.success(`${message}${link}`, null, { timeOut: 10000 });
    },
    [metric.id],
  );

  const currentUser = getCurrentUser(window.uData, isReadonlyUser);
  return (
    <StyledCssBaseline>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        {metric && (
          <>
            <KrRelationshipList
              currentUser={currentUser}
              baseUrl={workboardUrl}
              id={metric.id}
              enableSuggestions={isSuggestionEnabled}
              canEdit={canEdit}
              enableRAID
              enableSEM={isSemEnabled && !isIndividualGoal}
              onActionPlanClick={isWsActionPlanEnabled ? handleActionPlanClick : undefined}
              eventProperties={eventProperties}
              isEnhancedADOEnabled={!!window.wbConfig.isEnhancedADOKrrUIEnabled}
              isADOKRREnabled={!!window.wbConfig.isADOKRREnabled}
              adoFormOverride={
                window.wbConfig.isEnhancedADOKrrUIEnabled
                  ? ({ objectId, relationshipId, onSave, onClose, objectType }) => (
                      <Suspense fallback={<AdoModalLoader />}>
                        <FederatedAdoModal
                          open
                          objectId={String(objectId)}
                          relationshipId={String(relationshipId)}
                          onSave={(data) => {
                            // Your federated modal should persist changes itself
                            // Then call onSave to let the list refresh+close
                            onSave(data);
                          }}
                          onClose={onClose}
                          objectType={objectType}
                        />
                      </Suspense>
                    )
                  : undefined
              }
            />
            {actionPlanDialogOpen && (
              <Suspense fallback={null}>
                <ActionPlanDialog
                  relationshipId={`${metric.id}`}
                  userId={`${currentUser.id}`}
                  isCoAuthorActive={isCoauthorEnabled}
                  onClose={handleCloseActionPlan}
                  eventProperties={eventProperties}
                />
              </Suspense>
            )}
          </>
        )}
      </ThemeProvider>
    </StyledCssBaseline>
  );
}

export default KrRelationships;
